/**
 * Selección de COHORTE + solicitud de matrícula desde el detalle de curso del
 * catálogo (F2-A). Las cohortes ABIERTAS de mis academias llegan por sync
 * (academy_org); la solicitud va por REST (reglas open/waitlist ⇒ activación
 * inmediata con control de cupo server-side — lleno + waitlist encola FIFO;
 * approval ⇒ queda pendiente de un admin).
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CalendarRange, Users } from "lucide-solid";
import { handleApiResponse } from "@space/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { stableRows } from "@/lib/solid/stable-rows";
import { useActiveOrg } from "../hooks/use-active-org";
import { useMyCohortEnrollments, useOpenCohorts } from "../hooks/use-cohort-data";

export const CourseCohortEnroll: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const cohorts = useOpenCohorts(activeOrgId, () => props.courseId);
  // Identidad estable: los ecos de sync re-emiten objetos nuevos y el <For>
  // remontaría las filas (el botón "matricularme" se recrearía bajo el click).
  const stableCohorts = stableRows(() => cohorts.data ?? undefined, {
    ignore: ["updated_at", "created_at"],
  });
  const myEnrollments = useMyCohortEnrollments();

  const enrollmentFor = createMemo(() => {
    const map = new Map<string, string>();
    for (const e of myEnrollments.data ?? []) {
      if (e.cohort_id && e.status) {
        map.set(e.cohort_id, e.status);
      }
    }
    return map;
  });

  const request = async (cohortId: string) => {
    try {
      const res = await handleApiResponse(
        api.POST("/api/cohorts/{id}/request-enrollment", {
          params: { path: { id: cohortId } },
        }),
      );
      const status = (res as { status?: string } | undefined)?.status;
      if (status === "active") {
        showToast(t(msg`You are enrolled in the cohort!`), "success");
      } else if (status === "waitlisted") {
        showToast(t(msg`You are on the waitlist`), "success");
      } else {
        showToast(t(msg`Enrollment requested — pending approval`), "success");
      }
    } catch {
      showToast(t(msg`Could not request enrollment`), "error");
    }
  };

  return (
    <Show when={stableCohorts().length > 0}>
      <section class="space-y-3" data-testid="course-cohorts">
        <h2 class="font-display text-lg font-bold">{t(msg`Upcoming cohorts`)}</h2>
        <ul class="space-y-2">
          <For each={stableCohorts()}>
            {(c) => {
              const status = () => enrollmentFor().get(c.id);
              return (
                <li
                  class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border p-4"
                  data-testid="course-cohort-row"
                >
                  <div>
                    <p class="font-semibold" data-testid="course-cohort-name">
                      {c.name}
                    </p>
                    <p class="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <CalendarRange class="h-3.5 w-3.5" />
                      <Show when={c.start_date} fallback={<span>{c.modality}</span>}>
                        {c.start_date} → {c.end_date ?? "…"}
                      </Show>
                      <Show when={(c.capacity ?? 0) > 0}>
                        <span class="inline-flex items-center gap-1">
                          <Users class="h-3.5 w-3.5" /> {c.capacity}
                        </span>
                      </Show>
                    </p>
                  </div>
                  <Show
                    when={!status()}
                    fallback={
                      <span
                        class="rounded-full bg-accent/12 px-3 py-1 text-xs font-bold text-accent"
                        data-testid="course-cohort-status"
                      >
                        {status()}
                      </span>
                    }
                  >
                    <button
                      type="button"
                      data-testid="course-cohort-enroll"
                      onClick={() => void request(c.id)}
                      class="rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground transition-transform hover:scale-105 active:scale-95"
                    >
                      {t(msg`Enroll in this cohort`)}
                    </button>
                  </Show>
                </li>
              );
            }}
          </For>
        </ul>
      </section>
    </Show>
  );
};
